import { useEffect, useMemo, useState } from 'react';
import VaultPageHeader from '@/components/knowledge/VaultPageHeader';
import VaultPageList from '@/components/knowledge/VaultPageList';
import VaultPagePreview from '@/components/knowledge/VaultPagePreview';
import type { KnowledgeBaseVaultLink, KnowledgeBaseVaultPage } from '@/components/knowledge/types';
import { fetchVaultPageContent, slugifyVaultSegment } from '@/lib/librarian/api';
import { normalizeVaultTarget } from '@/lib/librarian/linkParser';

interface VaultBrowserProps {
  pages: KnowledgeBaseVaultPage[];
  links: KnowledgeBaseVaultLink[];
  selectedPageId?: string | null;
  onSelectPageId?: (pageId: string | null) => void;
}

export default function VaultBrowser({ pages, links, selectedPageId: controlledSelectedPageId, onSelectPageId }: VaultBrowserProps) {
  const [internalSelectedPageId, setInternalSelectedPageId] = useState<string | null>(null);
  const [contentByPageId, setContentByPageId] = useState<Record<string, string>>({});
  const [loadingPageId, setLoadingPageId] = useState<string | null>(null);
  const selectedPageId = controlledSelectedPageId ?? internalSelectedPageId;

  const updateSelectedPageId = (pageId: string | null) => {
    if (controlledSelectedPageId === undefined) {
      setInternalSelectedPageId(pageId);
    }
    onSelectPageId?.(pageId);
  };

  useEffect(() => {
    if (pages.length === 0) {
      return;
    }

    const selectedPageStillExists = selectedPageId ? pages.some((page) => page.id === selectedPageId) : false;
    if (selectedPageStillExists) {
      return;
    }

    const preferred = pages.find((page) => page.path === 'index.md') ?? pages[0];
    updateSelectedPageId(preferred.id);
  }, [pages, selectedPageId]);

  useEffect(() => {
    if (!selectedPageId || contentByPageId[selectedPageId] !== undefined) {
      return;
    }

    let cancelled = false;
    setLoadingPageId(selectedPageId);

    void (async () => {
      try {
        const content = await fetchVaultPageContent(selectedPageId);
        if (cancelled) return;
        setContentByPageId((current) => ({ ...current, [selectedPageId]: content }));
      } catch (error) {
        console.error('Failed to fetch vault page content:', error);
      } finally {
        if (!cancelled) {
          setLoadingPageId((current) => (current === selectedPageId ? null : current));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [contentByPageId, selectedPageId]);

  const selectedPage = useMemo(() => {
    const page = pages.find((entry) => entry.id === selectedPageId) ?? null;
    if (!page) {
      return null;
    }

    const resolvedContent = contentByPageId[page.id];
    return resolvedContent === undefined
      ? page
      : { ...page, content_markdown: resolvedContent };
  }, [contentByPageId, pages, selectedPageId]);

  const linkedPagesCount = useMemo(() => {
    if (!selectedPage) return 0;
    return links.filter((link) => link.from_page_id === selectedPage.id || link.to_page_id === selectedPage.id).length;
  }, [links, selectedPage]);

  const selectByTarget = (target: string) => {
    const normalizedTarget = normalizeVaultTarget(target);
    const normalizedWithoutExtension = normalizedTarget.replace(/\.md$/i, '');
    const pathSegments = normalizedWithoutExtension.split('/').filter(Boolean);
    const targetBaseName = pathSegments[pathSegments.length - 1] ?? normalizedWithoutExtension;
    const targetSlug = slugifyVaultSegment(targetBaseName);

    const linkedPageId = selectedPageId
      ? links.find((link) => (
        link.from_page_id === selectedPageId &&
        normalizeVaultTarget(link.target_path) === normalizedTarget &&
        link.to_page_id
      ))?.to_page_id ?? null
      : null;

    const page =
      (linkedPageId ? pages.find((entry) => entry.id === linkedPageId) ?? null : null) ??
      pages.find((entry) => entry.path === normalizedTarget) ??
      pages.find((entry) => normalizeVaultTarget(entry.path) === normalizedTarget) ??
      pages.find((entry) => entry.path.replace(/\.md$/i, '') === normalizedWithoutExtension) ??
      pages.find((entry) => entry.slug === targetBaseName || entry.slug === targetSlug) ??
      pages.find((entry) => slugifyVaultSegment(entry.title) === targetSlug) ??
      null;

    console.log('[vault-link-resolve] resolving internal vault link', {
      selectedPageId,
      target,
      normalizedTarget,
      linkedPageId,
      resolvedPageId: page?.id ?? null,
      resolvedPagePath: page?.path ?? null,
    });

    if (page) {
      updateSelectedPageId(page.id);
    }
  };

  return (
    <div className="grid h-full min-h-0 gap-4 overflow-hidden lg:grid-cols-[18rem_minmax(0,1fr)]">
      <div className="min-h-0 overflow-hidden">
        <VaultPageList
          pages={pages}
          selectedPageId={selectedPageId}
          onSelectPage={(page) => updateSelectedPageId(page.id)}
        />
      </div>
      <div className="flex min-h-0 flex-col gap-4 overflow-hidden">
        {selectedPage && <VaultPageHeader page={selectedPage} linkedPagesCount={linkedPagesCount} />}
        <div className="min-h-0 flex-1 overflow-hidden">
          <VaultPagePreview
            page={selectedPage}
            onNavigateInternal={selectByTarget}
            loading={Boolean(selectedPageId && loadingPageId === selectedPageId && !contentByPageId[selectedPageId])}
          />
        </div>
      </div>
    </div>
  );
}